import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import SystemHealthBar from '../ui/SystemHealthBar';
import DeviceSoftwareCard from './DeviceSoftwareCard';

interface DeviceOverviewTabProps {
  device: any;
  telemetry: any;
}

function formatUptime(secs: number | undefined | null): string {
  if (!secs) return '—';
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  return `${days}d ${hours}h ${minutes}m`;
}

function mbToGb(mb: number): string {
  return (mb / 1024).toFixed(1);
}

function signalStrengthLabel(dbm: number): { text: string; color: string } {
  if (dbm >= -50) return { text: 'Excellent', color: 'text-green-400' };
  if (dbm >= -60) return { text: 'Good', color: 'text-green-400' };
  if (dbm >= -70) return { text: 'Fair', color: 'text-yellow-400' };
  if (dbm >= -80) return { text: 'Weak', color: 'text-orange-400' };
  return { text: 'Very Weak', color: 'text-red-400' };
}

function gpsFixLabel(fixType: string | undefined): { text: string; color: string } {
  switch (fixType) {
    case '3d':
    case '3D':
      return { text: '3D Fix', color: 'text-green-400' };
    case '2d':
    case '2D':
      return { text: '2D Fix', color: 'text-yellow-400' };
    case 'dgps':
    case 'DGPS':
      return { text: 'DGPS', color: 'text-green-400' };
    case 'rtk':
    case 'RTK':
      return { text: 'RTK', color: 'text-green-400' };
    case 'none':
    case 'no_fix':
      return { text: 'No Fix', color: 'text-red-400' };
    default:
      return { text: fixType || '—', color: 'text-slate-400' };
  }
}

function hdopQuality(hdop: number | undefined | null): { text: string; color: string } {
  if (hdop == null) return { text: '—', color: 'text-slate-400' };
  if (hdop <= 1) return { text: 'Ideal', color: 'text-green-400' };
  if (hdop <= 2) return { text: 'Excellent', color: 'text-green-400' };
  if (hdop <= 5) return { text: 'Good', color: 'text-yellow-400' };
  if (hdop <= 10) return { text: 'Moderate', color: 'text-orange-400' };
  return { text: 'Poor', color: 'text-red-400' };
}

function DeviceOverviewTab({ device, telemetry }: DeviceOverviewTabProps) {
  const [copiedId, setCopiedId] = useState(false);

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(device.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  const ramPercent =
    telemetry?.memory_used_mb && telemetry?.memory_total_mb
      ? (telemetry.memory_used_mb / telemetry.memory_total_mb) * 100
      : 0;

  const diskPercent =
    telemetry?.disk_used_mb && telemetry?.disk_total_mb
      ? (telemetry.disk_used_mb / telemetry.disk_total_mb) * 100
      : 0;

  const ramUsedText =
    telemetry?.memory_used_mb && telemetry?.memory_total_mb
      ? `${mbToGb(telemetry.memory_used_mb)} GB used · ${mbToGb(telemetry.memory_total_mb - telemetry.memory_used_mb)} GB free of ${mbToGb(telemetry.memory_total_mb)} GB`
      : undefined;

  const diskUsedText =
    telemetry?.disk_used_mb && telemetry?.disk_total_mb
      ? `${mbToGb(telemetry.disk_used_mb)} GB used · ${mbToGb(telemetry.disk_total_mb - telemetry.disk_used_mb)} GB free of ${mbToGb(telemetry.disk_total_mb)} GB`
      : undefined;

  const hasBattery = telemetry?.battery_percent != null;
  const hasNetwork = telemetry?.network_type || telemetry?.wifi_signal_dbm != null;
  const hasGps = telemetry?.gps_fix_type || telemetry?.gps_satellites != null;
  const wifiSignal = telemetry?.wifi_signal_dbm != null ? signalStrengthLabel(telemetry.wifi_signal_dbm) : null;
  const gpsFix = gpsFixLabel(telemetry?.gps_fix_type);
  const hdop = hdopQuality(telemetry?.gps_hdop);

  // Data freshness
  const collectedAt = telemetry?.collected_at || telemetry?.recorded_at;

  return (
    <div className="space-y-6">
      {/* Data Freshness Indicator */}
      {collectedAt && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
          Telemetry collected {formatDistanceToNow(new Date(collectedAt), { addSuffix: true })}
        </div>
      )}

      {/* Section 1: Device Info + Owner */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Information Card */}
        <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
          <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
            Device Information
          </h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-slate-300">Serial</dt>
              <dd className="text-sm text-white font-medium">{device.serial_number}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-slate-300">Type</dt>
              <dd className="text-sm text-white font-medium">{device.device_type}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-slate-300">Registered</dt>
              <dd className="text-sm text-white font-medium">
                {device.created_at
                  ? format(new Date(device.created_at), 'd MMM yyyy')
                  : '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-slate-300">Registration Code</dt>
              <dd className="text-sm font-medium font-mono flex items-center gap-2">
                <span className="text-white">{device.registration_code || '—'}</span>
                {device.registration_code && device.registration_code_expires_at && new Date(device.registration_code_expires_at) < new Date() && (
                  <span className="text-xs text-red-400 font-medium">Expired</span>
                )}
              </dd>
            </div>
            {telemetry?.screen_width && telemetry?.screen_height && (
              <div className="flex justify-between">
                <dt className="text-sm text-slate-300">Screen</dt>
                <dd className="text-sm text-white font-medium">
                  {telemetry.screen_width} x {telemetry.screen_height}
                </dd>
              </div>
            )}
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-300">Device ID</dt>
              <dd className="text-sm text-white font-medium font-mono flex items-center gap-2">
                <span title={device.id}>
                  {device.id ? `${device.id.slice(0, 8)}...` : '—'}
                </span>
                {device.id && (
                  <button
                    onClick={handleCopyId}
                    className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
                    title="Copy full ID"
                  >
                    {copiedId ? 'Copied!' : 'Copy'}
                  </button>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Owner Card */}
        <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
          <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
            Owner
          </h3>
          <dl className="space-y-3">
            <div className="flex justify-between gap-3">
              <dt className="text-sm text-slate-300 shrink-0">Name</dt>
              <dd className="text-sm text-white font-medium text-right">
                {device.user?.name || device.user?.email || (device.user_id ? device.user_id : 'Unassigned')}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sm text-slate-300 shrink-0">Email</dt>
              <dd className="text-sm text-white font-medium text-right">
                {device.user?.email || '—'}
              </dd>
            </div>
            {(device.user?.id || device.user_id) && (
              <div className="flex justify-between gap-3 items-start">
                <dt className="text-sm text-slate-300 shrink-0">User ID</dt>
                <dd className="text-sm text-right">
                  <Link
                    to={`/admin/devices?owner=${encodeURIComponent(device.user?.id || device.user_id)}`}
                    className="font-mono text-cyan-400 hover:text-cyan-300 transition-colors"
                    title="Show all devices owned by this user"
                  >
                    {device.user?.id || device.user_id}
                  </Link>
                  <p className="text-xs text-slate-500 mt-1">
                    <Link
                      to={`/admin/devices?owner=${encodeURIComponent(device.user?.id || device.user_id)}`}
                      className="text-cyan-500/80 hover:text-cyan-300 transition-colors"
                    >
                      View all devices by this owner →
                    </Link>
                  </p>
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-sm text-slate-300">Subscription</dt>
              <dd className="text-sm text-white font-medium">
                {device.subscription_id ? 'Linked' : '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-slate-300">Last Seen</dt>
              <dd className="text-sm text-white font-medium">
                {device.last_seen_at
                  ? formatDistanceToNow(new Date(device.last_seen_at), { addSuffix: true })
                  : '—'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Software: app versions from the latest update check (Scouts only) */}
      <DeviceSoftwareCard device={device} />

      {/* Section 2: System Health */}
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
          System Health
        </h3>

        {/* Health bars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
          {telemetry?.cpu_usage_percent != null ? (
            <SystemHealthBar
              label="CPU"
              value={telemetry.cpu_usage_percent}
            />
          ) : (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">CPU</span>
                <span className="text-slate-500 font-medium">—</span>
              </div>
              <div className="h-2 rounded-full bg-slate-700 overflow-hidden" />
            </div>
          )}
          <SystemHealthBar
            label="RAM"
            value={ramPercent}
            usedText={ramUsedText}
          />
          <SystemHealthBar
            label="Disk"
            value={diskPercent}
            usedText={diskUsedText}
          />
          {hasBattery ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Battery</span>
                <span className="flex items-center gap-1.5">
                  {telemetry.battery_charging && (
                    <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                    </svg>
                  )}
                  <span className={`font-medium ${
                    telemetry.battery_percent > 50 ? 'text-green-400'
                    : telemetry.battery_percent > 20 ? 'text-yellow-400'
                    : 'text-red-400'
                  }`}>
                    {Math.round(telemetry.battery_percent)}%
                  </span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    telemetry.battery_percent > 50 ? 'bg-green-500'
                    : telemetry.battery_percent > 20 ? 'bg-yellow-500'
                    : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(100, telemetry.battery_percent)}%` }}
                />
              </div>
              <p className="text-xs text-slate-400">
                {telemetry.battery_charging ? 'Charging' : 'On battery'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Uptime</span>
              </div>
              <p className="text-lg font-medium text-slate-100">
                {formatUptime(telemetry?.uptime_secs)}
              </p>
            </div>
          )}
        </div>

        {/* CPU Temp (if available) */}
        {telemetry?.cpu_temp_celsius != null && (
          <div className="border-t border-slate-700 pt-4 mb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-300">CPU Temperature</span>
              <span className={`text-sm font-medium ${
                telemetry.cpu_temp_celsius < 60 ? 'text-green-400'
                : telemetry.cpu_temp_celsius < 80 ? 'text-yellow-400'
                : 'text-red-400'
              }`}>
                {telemetry.cpu_temp_celsius.toFixed(1)}°C
              </span>
            </div>
          </div>
        )}

        {/* Uptime (show below health bars if battery is present) */}
        {hasBattery && (
          <div className="border-t border-slate-700 pt-4 mb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-300">Uptime</span>
              <span className="text-sm font-medium text-slate-100">{formatUptime(telemetry?.uptime_secs)}</span>
            </div>
          </div>
        )}

        {/* Software version grid */}
        <div className="border-t border-slate-700 pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
            <div>
              <p className="text-xs text-slate-400">OS</p>
              <p className="text-sm text-white">{telemetry?.os_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Kernel</p>
              <p className="text-sm text-white">{telemetry?.os_kernel || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">LA Version</p>
              <p className="text-sm text-white">{telemetry?.la_version || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">App Version</p>
              <p className="text-sm text-white">{telemetry?.app_version || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Electron</p>
              <p className="text-sm text-white">{telemetry?.electron_version || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Packs</p>
              <p className="text-sm text-white">
                {telemetry?.packs_installed != null
                  ? `${telemetry.packs_installed} installed (${telemetry.packs_total_size_mb ?? 0} MB)`
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Connectivity & GPS */}
      {(hasNetwork || hasGps) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Network */}
          {hasNetwork && (
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
              <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
                Network
              </h3>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-slate-300">Connection</dt>
                  <dd className="text-sm text-white font-medium capitalize">
                    {telemetry.network_type || '—'}
                  </dd>
                </div>
                {telemetry.wifi_signal_dbm != null && wifiSignal && (
                  <>
                    <div className="flex justify-between">
                      <dt className="text-sm text-slate-300">WiFi Signal</dt>
                      <dd className="text-sm font-medium">
                        <span className={wifiSignal.color}>{wifiSignal.text}</span>
                        <span className="text-slate-400 ml-1.5">({telemetry.wifi_signal_dbm} dBm)</span>
                      </dd>
                    </div>
                    {/* Signal strength bar */}
                    <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          telemetry.wifi_signal_dbm >= -60 ? 'bg-green-500'
                          : telemetry.wifi_signal_dbm >= -70 ? 'bg-yellow-500'
                          : telemetry.wifi_signal_dbm >= -80 ? 'bg-orange-500'
                          : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(5, ((telemetry.wifi_signal_dbm + 100) / 60) * 100))}%` }}
                      />
                    </div>
                  </>
                )}
              </dl>
            </div>
          )}

          {/* GPS Quality */}
          {hasGps && (
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
              <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
                GPS Quality
              </h3>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-slate-300">Fix Type</dt>
                  <dd className={`text-sm font-medium ${gpsFix.color}`}>{gpsFix.text}</dd>
                </div>
                {telemetry.gps_satellites != null && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-300">Satellites</dt>
                    <dd className={`text-sm font-medium ${
                      telemetry.gps_satellites >= 8 ? 'text-green-400'
                      : telemetry.gps_satellites >= 4 ? 'text-yellow-400'
                      : 'text-red-400'
                    }`}>
                      {telemetry.gps_satellites}
                    </dd>
                  </div>
                )}
                {telemetry.gps_hdop != null && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-300">HDOP</dt>
                    <dd className="text-sm font-medium">
                      <span className={hdop.color}>{hdop.text}</span>
                      <span className="text-slate-400 ml-1.5">({telemetry.gps_hdop.toFixed(1)})</span>
                    </dd>
                  </div>
                )}
                {device.last_known_position && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-300">Position</dt>
                    <dd className="text-sm text-white font-medium font-mono">
                      {device.last_known_position.lat?.toFixed(5)}, {device.last_known_position.lon?.toFixed(5)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      )}

      {/* Section 4: Storage & Cache */}
      {(telemetry?.tile_cache_size_mb != null || telemetry?.packs_installed != null) && (
        <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
          <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
            Storage Breakdown
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {telemetry.disk_total_mb != null && (
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{mbToGb(telemetry.disk_total_mb)}</p>
                <p className="text-xs text-slate-400">Total (GB)</p>
              </div>
            )}
            {telemetry.disk_used_mb != null && (
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{mbToGb(telemetry.disk_used_mb)}</p>
                <p className="text-xs text-slate-400">Used (GB)</p>
              </div>
            )}
            {telemetry.tile_cache_size_mb != null && (
              <div className="text-center">
                <p className="text-2xl font-bold text-cyan-400">
                  {telemetry.tile_cache_size_mb >= 1024
                    ? `${mbToGb(telemetry.tile_cache_size_mb)}`
                    : telemetry.tile_cache_size_mb}
                </p>
                <p className="text-xs text-slate-400">
                  Tile Cache ({telemetry.tile_cache_size_mb >= 1024 ? 'GB' : 'MB'})
                </p>
              </div>
            )}
            {telemetry.packs_total_size_mb != null && (
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-400">
                  {telemetry.packs_total_size_mb >= 1024
                    ? mbToGb(telemetry.packs_total_size_mb)
                    : telemetry.packs_total_size_mb}
                </p>
                <p className="text-xs text-slate-400">
                  Packs ({telemetry.packs_total_size_mb >= 1024 ? 'GB' : 'MB'})
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 5: Recent Errors */}
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
          Recent Errors
        </h3>
        {telemetry?.recent_errors && telemetry.recent_errors.length > 0 ? (
          <ul className="space-y-2">
            {telemetry.recent_errors.map((error: string, index: number) => (
              <li
                key={index}
                className="rounded-md bg-red-900/20 border border-red-800/30 px-3 py-2 text-sm text-red-300"
              >
                {error}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No recent errors</p>
        )}
      </div>
    </div>
  );
}

export default DeviceOverviewTab;
